// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { WellFormedError } from '@/api/types';
import {
  CreateCostAdjustmentParameters,
  createCostAdjustment,
} from '@/asset/api/create-cost-adjustment';
import { CostAdjustment } from '@/asset/api/types';
import { CostAdjustmentDialog } from '@/asset/components/cost-adjustment-dialog';
import { CostAdjustmentTable } from '@/asset/components/cost-adjustment-table';
import { SupportSnackbar } from '@/components/support-snackbar';
import { AddActionIcon } from '@/constants/hardfin-standard-icons';
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

interface Props {
  assetKey: string;
  adjustments: CostAdjustment[];
  capitalizedCosts: Currency | null;
  costCurrencyCode: IsoCurrencyCode;
}

export function CostAdjustmentSection(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const dialog = useDisclosure();
  const [amountError, setAmountError] = useState<string | null>(null);
  const [apiError, setAPIError] = useState<WellFormedError | null>(null);
  // Stable so the dialog's amount-field subscription is not torn down every render.
  const clearAmountError = useCallback(() => setAmountError(null), []);

  const createMutation = useMutation({
    mutationFn: (parameters: CreateCostAdjustmentParameters) =>
      createCostAdjustment(props.assetKey, parameters),
    onSuccess: async () => {
      setAmountError(null);
      setAPIError(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['asset', props.assetKey],
        }),
        queryClient.invalidateQueries({
          queryKey: ['asset', props.assetKey, 'audit'],
        }),
      ]);
      dialog.close();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      setAPIError(apiError);

      if (apiError.errorType === 'asset_cost_adjustment_exceeds_book_value') {
        setAmountError(getUserFacingErrorMessage(apiError));
        return;
      }
      // Sentry must only fire for truly unmapped codes. Curated codes
      // (e.g. asset_invalid_cost_adjustment_type 1142 ... 1147) have
      // ERROR_MESSAGES entries and are expected validation errors.
      const curated = getUserFacingErrorMessage(apiError);
      if (
        curated === SUPPORT_FALLBACK_ERROR_MESSAGE &&
        !apiError.sentToSentry
      ) {
        captureException(error);
      }
    },
  });

  const showSnackbar =
    apiError !== null &&
    apiError?.errorType !== 'asset_cost_adjustment_exceeds_book_value';

  const handleClose = () => {
    setAmountError(null);
    setAPIError(null);
    createMutation.reset();
    dialog.close();
  };

  const handleSubmit = (parameters: CreateCostAdjustmentParameters) => {
    setAmountError(null);
    setAPIError(null);
    createMutation.reset();
    createMutation.mutate(parameters);
  };

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        mt: 4,
        pt: 3,
      }}
    >
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setAPIError(null);
          createMutation.reset();
        }}
        message="Unable to create cost adjustment."
      />
      <CostAdjustmentDialog
        amountError={amountError}
        capitalizedCosts={props.capitalizedCosts}
        costCurrencyCode={props.costCurrencyCode}
        isOpen={dialog.isOpen}
        isPending={createMutation.isPending}
        onClearAmountError={clearAmountError}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
      <Stack
        alignItems={{ sm: 'center', xs: 'flex-start' }}
        direction={{ sm: 'row', xs: 'column' }}
        justifyContent="space-between"
        mb={2}
        spacing={2}
      >
        <Typography variant="h6">Cost adjustments</Typography>
        <Button
          startIcon={<AddActionIcon />}
          variant="contained"
          onClick={dialog.open}
        >
          Add adjustment
        </Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Add cost adjustments to record prospective changes that{' '}
        <strong>update</strong> the asset's cost. To restate an asset's{' '}
        <strong>entire</strong> cost retroactively, update the asset's original
        cost basis.
      </Alert>
      <CostAdjustmentTable adjustments={props.adjustments} />
    </Box>
  );
}
